import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getColumnConfigs,
  saveColumnConfigs,
} from "../api/services/columnConfigService";
import type { ScreenName } from "../constants/screenNames";
import { useApp } from "../contexts/AppContext";

export interface DefaultColumn {
  key: string;
  defaultVisible: boolean;
  defaultOrder: number;
}

interface UseScreenColumnPreferencesResult {
  columns: { key: string; isVisible: boolean; order: number }[];
  visibleColumns: string[];
  savePreferences: (
    cols: { key: string; isVisible: boolean; order: number }[],
  ) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  isLoading: boolean;
}

export function useScreenColumnPreferences(
  screenName: ScreenName,
  defaultColumns: DefaultColumn[],
): UseScreenColumnPreferencesResult {
  const { mode } = useApp();
  const queryClient = useQueryClient();

  const defaultState = useMemo(
    () =>
      defaultColumns.map((c) => ({
        key: c.key,
        isVisible: c.defaultVisible,
        order: c.defaultOrder,
      })),
    [defaultColumns],
  );

  const [localColumns, setLocalColumns] = useState(defaultState);

  const { data: apiData, isLoading } = useQuery({
    queryKey: ["columnConfig"],
    queryFn: getColumnConfigs,
    enabled: mode === "live",
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (mode !== "live" || !apiData?.entries) {
      setLocalColumns(defaultState);
      return;
    }

    const screenEntries = apiData.entries.filter(
      (e) => e.screenName === screenName,
    );

    if (screenEntries.length === 0) {
      setLocalColumns(defaultState);
      return;
    }

    const savedMap = new Map(
      screenEntries.map((e) => [
        e.columnId,
        { isVisible: e.isVisible, order: e.order },
      ]),
    );

    const merged = defaultColumns.map((dc) => {
      const saved = savedMap.get(dc.key);
      if (saved) {
        return { key: dc.key, isVisible: saved.isVisible, order: saved.order };
      }
      return {
        key: dc.key,
        isVisible: dc.defaultVisible,
        order: dc.defaultOrder,
      };
    });

    merged.sort((a, b) => a.order - b.order);
    setLocalColumns(merged);
  }, [apiData, screenName, defaultColumns, defaultState, mode]);

  const visibleColumns = useMemo(
    () =>
      localColumns
        .filter((c) => c.isVisible)
        .sort((a, b) => a.order - b.order)
        .map((c) => c.key),
    [localColumns],
  );

  const savePreferences = useCallback(
    async (cols: { key: string; isVisible: boolean; order: number }[]) => {
      setLocalColumns(cols);

      if (mode === "live") {
        await saveColumnConfigs({
          screenName,
          columns: cols.map((c) => ({
            columnId: c.key,
            isVisible: c.isVisible,
            order: c.order,
          })),
        });
        await queryClient.invalidateQueries({ queryKey: ["columnConfig"] });
      }
    },
    [mode, screenName, queryClient],
  );

  const resetToDefaults = useCallback(async () => {
    setLocalColumns(defaultState);

    if (mode === "live") {
      await saveColumnConfigs({
        screenName,
        columns: defaultState.map((c) => ({
          columnId: c.key,
          isVisible: c.isVisible,
          order: c.order,
        })),
      });
      await queryClient.invalidateQueries({ queryKey: ["columnConfig"] });
    }
  }, [mode, screenName, defaultState, queryClient]);

  return {
    columns: localColumns,
    visibleColumns,
    savePreferences,
    resetToDefaults,
    isLoading,
  };
}
